import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useUser as useClerkUser } from '@clerk/clerk-react';
import { getCookie } from '@/lib/cookies';
import { getCurrencySymbol, type CurrencyCode } from '@/lib/currency';
import { calculateFamilyPrice } from '@/lib/familyUpgradePricing';
import { Check, Minus, Plus } from 'lucide-react';
import logo from '@/assets/logo.png';

/**
 * /checkout-new — design exploration of the pay-what-you-want checkout.
 *
 * Deliberately narrower than /subscribe and /subscribe-2: no plan cards, no
 * billing toggle, no promo field, no kit upsell. Two questions and a button.
 *
 * The amount is not wired to Stripe. Our subscription endpoint takes seat
 * counts and resolves fixed Stripe prices; an arbitrary recurring amount needs
 * a checkout session built from ad-hoc price_data, which is a backend change.
 * Until that exists this page is a preview and the signed-in CTA says so.
 */

const MARKETING_BASE_URL = 'https://granguard.org';

export const Route = createFileRoute('/checkout-new')({
  validateSearch: (search: Record<string, unknown>) => ({
    people: typeof search.people === 'string' ? search.people : undefined,
    amount: typeof search.amount === 'string' ? search.amount : undefined,
  }),
  component: CheckoutNew,
});

function CheckoutNew() {
  const search = Route.useSearch();
  const { user: clerkUser, isLoaded: clerkLoaded } = useClerkUser();

  const currencyCookie = (typeof document !== 'undefined' ? getCookie('gg_currency') : null) as CurrencyCode | null;
  const symbol = getCurrencySymbol(currencyCookie ?? 'USD');

  const peopleFromUrl = Math.floor(Number(search.people));
  const [people, setPeople] = useState(Number.isFinite(peopleFromUrl) && peopleFromUrl >= 1 ? peopleFromUrl : 1);
  const [forSomeoneElse, setForSomeoneElse] = useState(false);

  // Anchor on the real list price for this many accounts, so the suggested
  // amount stays honest if pricing moves.
  const anchor = useMemo(() => Math.round(calculateFamilyPrice(people, false)), [people]);
  const options = useMemo(
    () => [0, Math.max(1, Math.round(anchor / 2)), anchor, anchor * 2],
    [anchor],
  );

  const amountFromUrl = Number(search.amount);
  const hasUrlAmount = search.amount !== undefined && Number.isFinite(amountFromUrl) && amountFromUrl >= 0;
  const [amount, setAmount] = useState<number>(hasUrlAmount ? amountFromUrl : anchor);
  const [customOpen, setCustomOpen] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [previewNote, setPreviewNote] = useState(false);

  // Keep the choice sensible when the account count changes: anyone still
  // sitting on the suggested amount follows it up or down, everyone else keeps
  // the number they picked.
  const [followAnchor, setFollowAnchor] = useState(!hasUrlAmount);
  const effectiveAmount = followAnchor ? anchor : amount;

  const choose = (next: number) => {
    setFollowAnchor(false);
    setAmount(next);
    setCustomOpen(false);
  };

  const changePeople = (next: number) => {
    setPeople(Math.min(12, Math.max(1, next)));
  };

  const commitCustom = () => {
    const parsed = Number(customValue.replace(/[^0-9.]/g, ''));
    if (Number.isFinite(parsed) && parsed >= 0) {
      setFollowAnchor(false);
      setAmount(Math.round(parsed * 100) / 100);
    }
  };

  const format = (value: number) => `${symbol}${value % 1 === 0 ? value : value.toFixed(2)}`;
  const perPerson = people > 0 ? effectiveAmount / people : effectiveAmount;

  const handleContinue = () => {
    const params = new URLSearchParams({ people: String(people), amount: String(effectiveAmount) });
    if (!clerkUser) {
      window.location.assign(`/signup?redirectTo=${encodeURIComponent(`/checkout-new?${params.toString()}`)}`);
      return;
    }
    setPreviewNote(true);
  };

  if (!clerkLoaded) return <div className="min-h-screen bg-white" />;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <style>{`
        @keyframes gg-total-in { from { opacity: 0.35; } to { opacity: 1; } }
        .gg-total { animation: gg-total-in 0.22s ease-out; }
        @media (prefers-reduced-motion: reduce) { .gg-total { animation: none; } }
      `}</style>

      <div className="mx-auto w-full max-w-[34rem] px-5 pb-16 pt-8 sm:pt-14">
        <div className="flex items-center justify-between gap-4">
          <img src={logo} alt="GranGuard" className="w-32" />
          <p className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-500">Design preview</p>
        </div>

        <header className="mt-10">
          <h1 className="text-[2rem] font-semibold leading-[1.15] text-slate-900 sm:text-[2.375rem]">
            Pay what you want
          </h1>
          <p className="mt-3 text-lg leading-relaxed text-slate-600">
            GranGuard is new, and you haven't used it yet. So pick what it's worth to you. You can change it
            whenever you like.
          </p>
        </header>

        {/* ---- 1. How many accounts ---- */}
        <section className="mt-10 border-t border-slate-200 pt-8">
          <h2 className="text-base font-semibold text-slate-900">Who are you protecting?</h2>

          <div className="mt-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-lg font-medium text-slate-900">
                {people} {people === 1 ? 'account' : 'accounts'}
              </p>
              <p className="mt-0.5 text-base text-slate-500">
                {forSomeoneElse
                  ? `For ${people === 1 ? 'someone you care about' : 'people you care about'}, plus a free account for you`
                  : people === 1
                    ? 'Just you'
                    : `You and ${people - 1} ${people === 2 ? 'other' : 'others'}`}
              </p>
            </div>

            <div className="inline-flex shrink-0 items-center gap-1 rounded-2xl p-1 ring-1 ring-slate-200">
              <button
                type="button"
                onClick={() => changePeople(people - 1)}
                disabled={people <= 1}
                aria-label="Fewer accounts"
                className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl text-slate-700 transition hover:bg-slate-50 disabled:cursor-default disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
              >
                <Minus className="h-5 w-5" />
              </button>
              <span
                className="w-9 text-center text-xl font-semibold tabular-nums"
                aria-live="polite"
                aria-atomic="true"
              >
                {people}
              </span>
              <button
                type="button"
                onClick={() => changePeople(people + 1)}
                disabled={people >= 12}
                aria-label="More accounts"
                className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl text-slate-700 transition hover:bg-slate-50 disabled:cursor-default disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
          </div>

          <label className="mt-4 flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={forSomeoneElse}
              onChange={(e) => setForSomeoneElse(e.target.checked)}
              className="mt-1 h-5 w-5 shrink-0 cursor-pointer rounded border-slate-300 accent-brand-600"
            />
            <span className="text-base text-slate-600">
              I'm setting this up for someone else. Your own account is free.
            </span>
          </label>
        </section>

        {/* ---- 2. How much ---- */}
        <section className="mt-10 border-t border-slate-200 pt-8">
          <h2 className="text-base font-semibold text-slate-900">What would you like to pay?</h2>

          <div className="mt-4 flex flex-wrap gap-2" role="radiogroup" aria-label="Monthly amount">
            {options.map((value) => {
              const selected = !customOpen && effectiveAmount === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => choose(value)}
                  className={`h-12 min-w-[4.25rem] cursor-pointer rounded-2xl px-4 text-lg font-medium tabular-nums transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 ${
                    selected
                      ? 'bg-brand-600 text-white'
                      : 'text-slate-900 ring-1 ring-slate-200 hover:ring-slate-400'
                  }`}
                >
                  {format(value)}
                </button>
              );
            })}
            <button
              type="button"
              role="radio"
              aria-checked={customOpen}
              onClick={() => {
                setCustomOpen(true);
                setCustomValue(String(effectiveAmount));
              }}
              className={`h-12 cursor-pointer rounded-2xl px-4 text-lg font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 ${
                customOpen ? 'bg-brand-600 text-white' : 'text-slate-900 ring-1 ring-slate-200 hover:ring-slate-400'
              }`}
            >
              Other
            </button>
          </div>

          {customOpen && (
            <div className="mt-3 flex items-center rounded-2xl ring-1 ring-slate-200 focus-within:ring-2 focus-within:ring-brand-600">
              <span className="pl-4 pr-1 text-lg text-slate-500">{symbol}</span>
              <label htmlFor="pwyw-amount" className="sr-only">
                Amount per month
              </label>
              <input
                id="pwyw-amount"
                type="text"
                inputMode="decimal"
                autoFocus
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value.replace(/[^0-9.]/g, ''))}
                onBlur={commitCustom}
                onKeyDown={(e) => e.key === 'Enter' && commitCustom()}
                className="h-12 w-full bg-transparent pr-4 text-lg tabular-nums text-slate-900 focus:outline-none"
              />
              <span className="whitespace-nowrap pr-4 text-base text-slate-500">a month</span>
            </div>
          )}

          <p className="mt-4 text-base leading-relaxed text-slate-500">
            Most people pay around {format(anchor)}. Pick {format(options[3])} and you're covering someone who can't.
            If money is tight, {format(0)} is a real option and you get exactly the same thing.
          </p>
        </section>

        {/* ---- 3. Total and go ---- */}
        <section className="mt-10 border-t border-slate-200 pt-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-base text-slate-500">Your total</p>
              <p className="mt-1 text-base text-slate-500">
                {people > 1 ? `${format(Math.round(perPerson * 100) / 100)} per account. ` : ''}Cancel whenever you
                like.
              </p>
            </div>
            <p key={effectiveAmount} className="gg-total shrink-0 text-right text-4xl font-semibold tabular-nums">
              {format(effectiveAmount)}
              <span className="ml-1 text-lg font-normal text-slate-500">/mo</span>
            </p>
          </div>

          <button
            type="button"
            onClick={handleContinue}
            className="mt-6 flex h-14 w-full cursor-pointer items-center justify-center rounded-2xl bg-brand-600 text-lg font-semibold text-white transition hover:bg-brand-700 active:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
          >
            {clerkUser ? 'Continue' : 'Create your account'}
          </button>

          {previewNote && (
            <p className="mt-3 text-center text-base text-slate-500" role="status">
              This is a design preview. Pay what you want isn't connected to Stripe yet.
            </p>
          )}

          <p className="mt-4 text-center text-sm leading-relaxed text-slate-400">
            Secure checkout by Stripe&ensp;·&ensp;
            <a
              href={`${MARKETING_BASE_URL}/policies#terms`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-slate-600"
            >
              Terms
            </a>
            &ensp;·&ensp;
            <a
              href={`${MARKETING_BASE_URL}/policies#privacy`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-slate-600"
            >
              Privacy
            </a>
          </p>
        </section>

        {/* ---- What you get ---- */}
        <section className="mt-10 border-t border-slate-200 pt-8">
          <h2 className="text-base font-semibold text-slate-900">What's included</h2>
          <ul className="mt-4 space-y-3">
            {[
              'Interactive scam-defense training',
              'Scam checker for links, texts, and emails',
              'Data breach monitoring',
              'Expert guidance when something feels off',
            ].map((item) => (
              <li key={item} className="flex gap-3 text-base text-slate-600">
                <Check className="mt-1 h-4 w-4 shrink-0 text-brand-600" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-base leading-relaxed text-slate-500">
            We're building this to make security something your parents can actually follow. Nothing here is behind
            a bigger plan, and there's no upsell waiting on the other side.
          </p>
        </section>
      </div>
    </div>
  );
}
